import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AppearanceMenu } from "@/components/AppearanceMenu";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { fetchSubjects, formatError } from "@/lib/api";

const SIDE_IMG = "https://images.unsplash.com/photo-1631556759511-6ce895fbf0ad?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000";

export function Shell({ title, subtitle, children }) {
  return (
    <div className="grid min-h-screen bg-canvas lg:grid-cols-2">
      <div className="flex flex-col px-6 py-8 sm:px-12">
        <div className="flex items-center justify-between"><Logo className="h-10" /><AppearanceMenu /></div>
        <div className="mx-auto my-auto w-full max-w-md py-12 fade-up">
          <h1 className="text-3xl font-extrabold tracking-tight text-ink">{title}</h1>
          <p className="mt-2 text-sm text-ink-2">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
      <div className="relative hidden lg:block">
        <img src={SIDE_IMG} alt="Clinical research team" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-band/70" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--brand-cyan)]">IDDCR LearnAI</p>
          <p className="mt-3 font-display text-3xl font-bold leading-tight">Learn it. Practise it. Prove it in your next interview.</p>
        </div>
      </div>
    </div>
  );
}

const FormError = ({ msg }) => msg ? <p data-testid="auth-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{msg}</p> : null;

export function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!user) return;
    const home = { admin: "/admin", employer: "/employer" }[user.role] || "/dashboard";
    const from = loc.state?.from;
    nav(from && from.startsWith("/employer") === (user.role === "employer") ? from : home, { replace: true });
  }, [user, nav, loc.state]);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await login(form.email, form.password);
    } catch (e2) { setErr(formatError(e2)); setBusy(false); }
  };
  return (
    <Shell title="Welcome back" subtitle="Log in to continue your learning.">
      <form onSubmit={submit} className="flex flex-col gap-5" data-testid="login-form">
        <div className="flex flex-col gap-2"><Label htmlFor="email">Email</Label>
          <Input id="email" data-testid="login-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        <div className="flex flex-col gap-2"><div className="flex items-center justify-between"><Label htmlFor="password">Password</Label>
          <Link data-testid="forgot-password-link" to="/forgot-password" className="text-xs font-semibold text-aqua hover:underline">Forgot password?</Link></div>
          <Input id="password" data-testid="login-password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
        <FormError msg={err} />
        <Button data-testid="login-submit-btn" type="submit" disabled={busy} className="h-11 rounded-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}</Button>
        <p className="text-sm text-ink-2">New here? <Link data-testid="goto-register-link" to="/register" className="font-semibold text-aqua hover:underline">Create an account</Link></p>
        <p className="text-sm text-ink-2">Hiring? <Link data-testid="goto-employer-register-link" to="/employer/register" className="font-semibold text-ink hover:underline">Register as an employer</Link></p>
      </form>
    </Shell>
  );
}

export function Register() {
  const { register, user } = useAuth();
  const nav = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", preferred_subjects: [] });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { fetchSubjects().then(setSubjects).catch(() => {}); }, []);
  useEffect(() => { if (user) nav(user.role === "employer" ? "/employer" : "/dashboard", { replace: true }); }, [user, nav]);

  const toggle = (id) => setForm((f) => ({ ...f, preferred_subjects: f.preferred_subjects.includes(id) ? f.preferred_subjects.filter((x) => x !== id) : [...f.preferred_subjects, id] }));
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (form.password.length < 8) return setErr("Password must be at least 8 characters.");
    setBusy(true);
    try { await register(form); nav("/dashboard", { replace: true }); } catch (e2) { setErr(formatError(e2)); setBusy(false); }
  };
  return (
    <Shell title="Create your account" subtitle="Free access to AI explanations, quizzes and flashcards.">
      <form onSubmit={submit} className="flex flex-col gap-5" data-testid="register-form">
        <div className="flex flex-col gap-2"><Label htmlFor="name">Full name</Label>
          <Input id="name" data-testid="register-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div className="flex flex-col gap-2"><Label htmlFor="remail">Email</Label>
          <Input id="remail" data-testid="register-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        <div className="flex flex-col gap-2"><Label htmlFor="rpw">Password</Label>
          <Input id="rpw" data-testid="register-password-input" type="password" required placeholder="At least 8 characters" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
        <div className="flex flex-col gap-2">
          <Label>Subjects you're interested in</Label>
          <div className="flex flex-wrap gap-2">
            {subjects.map((s) => (
              <button type="button" key={s.id} data-testid={`register-subject-${s.id}`} onClick={() => toggle(s.id)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${form.preferred_subjects.includes(s.id) ? "border-aqua bg-aqua-soft text-aqua" : "border-line text-ink-2 hover:border-ink-3"}`}>
                {s.name}
              </button>
            ))}
          </div>
        </div>
        <FormError msg={err} />
        <Button data-testid="register-submit-btn" type="submit" disabled={busy} className="h-11 rounded-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}</Button>
        <p className="text-sm text-ink-2">Already registered? <Link data-testid="goto-login-link" to="/login" className="font-semibold text-aqua hover:underline">Log in</Link></p>
      </form>
    </Shell>
  );
}
